// Collection Filter JavaScript - matches Hydrogen's filter modal functionality
class CollectionFilter {
  constructor(filters) {
    this.filters = filters || [];
    this.selectedFilters = {};
    this.showFilterModal = false;
    this.loadFiltersFromURL();
    this.init();
  }

  init() {
    // Make functions globally accessible
    window.showFilterModal = () => this.showModal();
    window.hideFilterModal = () => this.hideModal();
    window.handleFilterChange = (
      filterId,
      filterValue,
      isChecked,
      isBooleanFilter
    ) =>
      this.handleFilterChange(
        filterId,
        filterValue,
        isChecked,
        isBooleanFilter
      );
    window.clearAllFilters = () => this.clearAllFilters();
  }

  showModal() {
    this.showFilterModal = true;
    this.createModal();
  }

  hideModal() {
    this.showFilterModal = false;
    const modal = document.getElementById("filter-modal");
    if (modal) {
      modal.remove();
    }
  }

  createModal() {
    // Remove existing modal if any
    const existingModal = document.getElementById("filter-modal");
    if (existingModal) {
      existingModal.remove();
    }

    // Create modal HTML matching Hydrogen exactly
    const modal = document.createElement("div");
    modal.id = "filter-modal";
    modal.className = "fixed inset-0 z-40";
    modal.onclick = () => this.hideModal();
    modal.onkeydown = (e) => e.key === "Escape" && this.hideModal();
    modal.setAttribute("role", "button");
    modal.setAttribute("tabindex", "0");

    const modalContent = document.createElement("div");
    modalContent.className =
      "absolute top-0 left-0 mt-2 ml-6 bg-white border shadow-lg rounded-lg w-80 max-h-[80vh] overflow-auto z-50";
    modalContent.onclick = (e) => e.stopPropagation();
    modalContent.onkeydown = (e) => e.stopPropagation();
    modalContent.setAttribute("role", "presentation");

    // Modal header
    const header = document.createElement("div");
    header.className = "flex justify-between items-center p-4 border-b";
    header.innerHTML = `
      <h3 class="text-lg font-semibold">Filter</h3>
      <button onclick="hideFilterModal()" class="text-gray-500 hover:text-gray-700 text-xl cursor-pointer">×</button>
    `;

    // Modal body with filters
    const body = document.createElement("div");
    body.className = "p-6";

    // Get filters from constructor parameter
    if (this.filters && this.filters.length > 0) {
      const filtersToShow = this.filters.filter(
        (filter) => filter.id !== "filter.p.m.custom.series"
      );

      filtersToShow.forEach((filter) => {
        const filterDiv = document.createElement("div");
        filterDiv.className = "mb-6";

        // Handle boolean vs list filters
        const isBooleanFilter = filter.type === "boolean";

        if (isBooleanFilter) {
          // Boolean filter - use true_value
          if (!filter.true_value) {
            return; // Skip if no true_value
          }

          const filterValue = filter.true_value.value;
          const isChecked =
            this.selectedFilters[filter.id]?.includes(filterValue) || false;

          filterDiv.innerHTML = `
            <label class="flex items-center space-x-2 cursor-pointer">
              <input 
                type="checkbox" 
                ${isChecked ? "checked" : ""}
                onchange="handleFilterChange('${
                  filter.id
                }', '${encodeURIComponent(filterValue)}', this.checked, true)"
                class="rounded border-gray-300 text-secondary focus:ring-secondary"
              />
              <span class="font-medium">${filter.label} (${
            filter.true_value.count
          })</span>
            </label>
          `;
        } else {
          // List filter - use values array
          if (!filter.values || filter.values.length === 0) {
            return; // Skip if no values
          }

          let optionsHTML = "";
          filter.values.forEach((option) => {
            const filterValue = option.value; // Use the actual value
            const isChecked =
              this.selectedFilters[filter.id]?.includes(filterValue) || false;
            optionsHTML += `
              <label class="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  ${isChecked ? "checked" : ""}
                  onchange="handleFilterChange('${
                    filter.id
                  }', '${encodeURIComponent(
              filterValue
            )}', this.checked, false)"
                  class="rounded border-gray-300 text-secondary focus:ring-secondary"
                />
                <span class="text-sm">${option.label} (${option.count})</span>
              </label>
            `;
          });

          filterDiv.innerHTML = `
            <h4 class="font-medium mb-3">${filter.label}</h4>
            <div class="space-y-2">
              ${optionsHTML}
            </div>
          `;
        }

        body.appendChild(filterDiv);
      });
    } else {
      // No filters available
      body.innerHTML =
        '<p class="text-gray-500 text-center">No filters available</p>';
    }

    // Modal footer with clear all button
    const footer = document.createElement("div");
    footer.className = "p-4 border-t flex justify-center";
    footer.innerHTML = `
      <button 
        type="button" 
        onclick="clearAllFilters()" 
        class="px-4 py-2 text-gray-600 hover:text-gray-800 cursor-pointer"
      >
        Clear All
      </button>
    `;

    modalContent.appendChild(header);
    modalContent.appendChild(body);
    modalContent.appendChild(footer);
    modal.appendChild(modalContent);

    document.body.appendChild(modal);
  }

  handleFilterChange(filterId, filterValue, isChecked, isBooleanFilter) {
    // Decode the filter value since it comes URL-encoded from the HTML
    const decodedFilterValue = decodeURIComponent(filterValue);

    if (!this.selectedFilters[filterId]) {
      this.selectedFilters[filterId] = [];
    }

    if (isBooleanFilter) {
      // For boolean filters, use the actual label value (e.g., "Yes")
      this.selectedFilters[filterId] = isChecked ? [decodedFilterValue] : [];
    } else {
      // For multi-option filters, add/remove from array
      if (isChecked) {
        if (!this.selectedFilters[filterId].includes(decodedFilterValue)) {
          this.selectedFilters[filterId].push(decodedFilterValue);
        }
      } else {
        this.selectedFilters[filterId] = this.selectedFilters[filterId].filter(
          (value) => value !== decodedFilterValue
        );
      }
    }

    // Apply filters immediately by updating URL
    this.applyFilters();
  }

  clearAllFilters() {
    this.selectedFilters = {};
    this.applyFilters();
    this.hideModal();
  }

  loadFiltersFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    this.selectedFilters = {};

    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith("filter.") && key !== "filter.p.m.custom.series") {
        if (!this.selectedFilters[key]) {
          this.selectedFilters[key] = [];
        }
        this.selectedFilters[key].push(value);
      }
    }
  }

  applyFilters() {
    const currentURL = new URL(window.location);
    const searchParams = currentURL.searchParams;

    // Remove all existing filter parameters (except series)
    for (const [key] of Array.from(searchParams.entries())) {
      if (key.startsWith("filter.") && key !== "filter.p.m.custom.series") {
        searchParams.delete(key);
      }
    }

    // Add new filter parameters
    Object.entries(this.selectedFilters).forEach(([filterId, values]) => {
      if (values.length > 0) {
        values.forEach((value) => {
          searchParams.append(filterId, value);
        });
      }
    });

    // Update URL without reloading
    window.history.replaceState({}, "", currentURL.toString());

    // Fetch filtered products via AJAX
    this.fetchFilteredProducts(currentURL.toString());
  }

  async fetchFilteredProducts(url) {
    try {
      // Show loading state
      const productGrid = document.querySelector("#products-grid");
      if (productGrid) {
        productGrid.style.opacity = "0.5";
      }

      const response = await fetch(url);
      const html = await response.text();

      // Parse the response to extract the updated content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const newProductGrid = doc.querySelector("#products-grid");
      const newProductCount = doc.querySelector("#product-count");

      if (newProductGrid) {
        // Update the product grid
        const currentProductGrid = document.querySelector("#products-grid");
        if (currentProductGrid) {
          currentProductGrid.innerHTML = newProductGrid.innerHTML;
          currentProductGrid.style.opacity = "1";
        }
      }

      if (newProductCount) {
        // Update the product count
        const currentProductCount = document.querySelector("#product-count");
        if (currentProductCount) {
          currentProductCount.textContent = newProductCount.textContent;
        }
      }

      // Update series filtering if needed
      if (window.updateSeriesSelection) {
        const seriesButtons = document.querySelectorAll(
          ".series-btn, .series-btn-mobile"
        );
        const activeButton = Array.from(seriesButtons).find((btn) =>
          btn.classList.contains("border-secondary")
        );
        if (activeButton) {
          window.updateSeriesSelection(activeButton.dataset.series);
        }
      }
    } catch (error) {
      console.error("Error fetching filtered products:", error);
      // Remove loading state and show error
      const productGrid = document.querySelector("#products-grid");
      if (productGrid) {
        productGrid.style.opacity = "1";
      }
      // Fallback to page reload if AJAX fails
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }
}

// Initialize function that can be called from template
window.initCollectionFilter = function (filters) {
  new CollectionFilter(filters);
};
